import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import { reloadRecord } from './save-record.ts';
import type { ObservableDocument } from '../data/resources/observable-document.type.ts';

/**
 * Observable-document mutations require a caller-owned reload. Dispute sends only { note }, without
 * a TAS actor; reflect, rerun, and restore are bodyless admin POSTs.
 */

const observableDocumentsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'observable-documents' },
  op: 'query',
  resourcePath: 'observable-documents',
};

function observableDocumentsUrl(path?: string): string {
  const base = buildBaseURL(observableDocumentsUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * PATCH /observable-documents/:id/transition/subsistence-state/dispute
 * body `{ note }`
 *
 * Mirrors `ObservableDocumentAdapter#dispute`, which built the URL via
 * `this.buildURL('observable-document', id) + '/transition/subsistence-state/dispute'`
 * and sent `this.ajax(url, 'PATCH', { data: { note } })` (Ember Data's `data`
 * option IS the request body, not a JSON:API envelope).
 */
export function disputeObservableDocumentRequest(id: string, note: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/transition/subsistence-state/dispute`),
    method: 'PATCH',
    body: JSON.stringify({ note }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Disputes a document and refreshes its cached record. The explicit store supports owner-free
 * resource callers.
 */
export async function disputeObservableDocument(
  store: Store,
  doc: ObservableDocument,
  note: string
): Promise<ObservableDocument> {
  await store.request(disputeObservableDocumentRequest(doc.id as string, note));
  return reloadRecord(store, doc);
}

/**
 * DELETE /observable-documents/:id
 *
 * Mirrors `ObservableDocumentAdapter#discardObservableDocument`, which built
 * the URL via `this.buildURL('observable-documents') + '/' + id` and sent
 * `this.ajax(url, 'DELETE')` (no body).
 */
export function discardObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(id),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /observable-documents/:id/reflect  (admin-only surface, NO body)
 *
 * Mirrors `ObservableDocumentAdapter#reflect` (tio-admin adapter), which built
 * the URL via `this.buildURL('observable-documents') + '/' + id + '/reflect'`
 * and sent `this.ajax(url, 'POST')` with no body. Route: config/routes.rb:278
 * `post 'reflect'` (member). No request body is emitted here either.
 */
export function reflectObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/reflect`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /observable-documents/:id/rerun  (admin-only surface, NO body)
 *
 * Mirrors `ObservableDocumentAdapter#rerun` (tio-admin adapter), which built the
 * URL via `this.buildURL('observable-documents') + '/' + id + '/rerun'` and sent
 * `this.ajax(url, 'POST')` with no body. Route: config/routes.rb:279
 * `post 'rerun'` (member). No request body is emitted here either.
 */
export function rerunObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/rerun`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}
